import type { ItemDto, ListDto, ShoppingSuggestionDto } from "@home/shared";
import { useMemo, useRef, useState } from "react";
import { copyList } from "../actions.ts";
import { api, fileUrl } from "../api.ts";
import { ItemEditor } from "../components/ItemEditor.tsx";
import { ReceiptSheet } from "../components/ReceiptSheet.tsx";
import { Sheet } from "../components/Sheet.tsx";
import { KIND_ICONS, t, unitLabel } from "../i18n.ts";
import { parseQuickItem, sameNameKey } from "../quickAdd.ts";
import { engine, listItems, memberName, useSync } from "../store.ts";
import { newId } from "../sync/engine.ts";
import { confirmDialog, hapticSelect, hapticTap } from "../telegram.ts";
import { useRemote } from "../useRemote.ts";

/** Сколько подсказок «Пора докупить» показывать над списком. */
const SUGGEST_SHOWN = 8;

interface Props {
  id: string;
  onBack: () => void;
  onOpen: (id: string) => void;
}

export function ListPage({ id, onBack, onOpen }: Props) {
  const { state, me } = useSync();
  const list = state.lists[id];
  const items = listItems(state, id);
  const [editing, setEditing] = useState<ItemDto | null>(null);
  const [menu, setMenu] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [checking, setChecking] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [open, bought] = useMemo(() => [items.filter((i) => !i.isChecked), items.filter((i) => i.isChecked)], [items]);

  if (!list || list.deletedAt || !me) {
    return (
      <main className="cards">
        <p className="empty">…</p>
        <button className="btn" onClick={onBack}>
          ←
        </button>
      </main>
    );
  }

  const canManage = list.ownerUserId === me.userId || me.role === "admin";
  const showToast = (text: string) => {
    setToast(text);
    setTimeout(() => setToast(null), 2500);
  };

  const toggle = (item: ItemDto) => {
    hapticSelect();
    void engine.mutate({ type: "item.update", id: item.id, isChecked: !item.isChecked });
  };

  return (
    <>
      <header className="page-header list-header">
        <button className="icon-btn back-btn" onClick={onBack} aria-label="back">
          ←
        </button>
        <div className="list-title" onClick={() => canManage && setRenaming(true)}>
          <span>{KIND_ICONS[list.kind]}</span> {list.name}
          {list.visibility === "private" && <span className="badge">🔒</span>}
        </div>
        {!list.isTemplate && me.role !== "child" && (
          <button className="icon-btn" onClick={() => setChecking(true)} aria-label={t("checkReceipt")} title={t("checkReceipt")}>
            🧾
          </button>
        )}
        <button className="icon-btn" onClick={() => setMenu(true)} aria-label="menu">
          ⋯
        </button>
      </header>

      {!list.isTemplate && items.length > 0 && (
        <div className="list-progress">
          <span className="progress">
            <span style={{ width: `${(bought.length / items.length) * 100}%` }} />
          </span>
          <span>
            {bought.length}/{items.length}
          </span>
        </div>
      )}

      <AddItem listId={id} />
      {!list.isTemplate && me.role !== "child" && <Suggestions list={list} items={items} />}

      <main className="items">
        {items.length === 0 && <p className="empty">{t("empty")}</p>}
        {open.map((item) => (
          <ItemRow key={item.id} item={item} token={me.fileToken} onToggle={toggle} onEdit={setEditing} />
        ))}
        {bought.length > 0 && (
          <>
            <div className="section-title">
              {t("bought")} · {bought.length}
            </div>
            {bought.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                token={me.fileToken}
                onToggle={toggle}
                onEdit={setEditing}
                checkedBy={item.checkedById && item.checkedById !== me.userId ? memberName(me.members, item.checkedById) : null}
              />
            ))}
          </>
        )}
      </main>

      {toast && <div className="toast">{toast}</div>}

      {editing && <ItemEditor item={state.items[editing.id] ?? editing} token={me.fileToken} onClose={() => setEditing(null)} />}

      {checking && <ReceiptSheet listId={list.id} items={items} onClose={() => setChecking(false)} />}

      {renaming && <RenameSheet list={list} onClose={() => setRenaming(false)} />}

      {menu && (
        <Sheet title={list.name} onClose={() => setMenu(false)}>
          <div className="menu">
            {!list.isTemplate && (
              <button
                className="row-button"
                onClick={async () => {
                  setMenu(false);
                  await copyList(list.id, list.name, true);
                  showToast(t("savedAsTemplate"));
                }}
              >
                ⭐ {t("saveAsTemplate")}
              </button>
            )}
            {list.isTemplate && (
              <button
                className="row-button"
                onClick={async () => {
                  setMenu(false);
                  onOpen(await copyList(list.id, list.name, false));
                }}
              >
                🛒 {t("useTemplate")}
              </button>
            )}
            {bought.length > 0 && (
              <>
                <button
                  className="row-button"
                  onClick={() => {
                    setMenu(false);
                    for (const i of bought) void engine.mutate({ type: "item.update", id: i.id, isChecked: false });
                  }}
                >
                  ↩️ {t("uncheckAll")}
                </button>
                <button
                  className="row-button"
                  onClick={() => {
                    setMenu(false);
                    for (const i of bought) void engine.mutate({ type: "item.delete", id: i.id });
                  }}
                >
                  🧹 {t("clearBought")}
                </button>
              </>
            )}
            {canManage && (
              <>
                <button
                  className="row-button"
                  onClick={() => {
                    setMenu(false);
                    setRenaming(true);
                  }}
                >
                  ✏️ {t("rename")}
                </button>
                <button
                  className="row-button danger"
                  onClick={async () => {
                    if (!(await confirmDialog(t("confirmDelete", { name: list.name }), t("delete")))) return;
                    setMenu(false);
                    void engine.mutate({ type: "list.delete", id: list.id });
                    onBack();
                  }}
                >
                  🗑 {t("deleteList")}
                </button>
              </>
            )}
          </div>
        </Sheet>
      )}
    </>
  );
}

/** Подходит ли подсказка к списку: в аптечный — лекарства, в детский — детское, в остальные — всё, кроме аптеки. */
function fitsList(kind: ListDto["kind"], category: string): boolean {
  if (kind === "pharmacy") return category === "health";
  if (kind === "kids") return category === "kids";
  return category !== "health";
}

/**
 * «Пора докупить»: что закончилось (отметили в холодильнике) и что по обычному ритму покупок пора брать снова.
 * Нажатие — позиция в списке (через движок синхронизации, работает и без сети), подсказка исчезает. Что уже
 * есть в этом списке — и отмеченное «куплено» (покупку ещё могли не записать), — не предлагаем.
 */
function Suggestions({ list, items }: { list: ListDto; items: ItemDto[] }) {
  const remote = useRemote("suggestions", () => api.pantrySuggestions().then((r) => r.items));
  const have = new Set(items.map((i) => sameNameKey(i.name)));
  const shown = (remote.data ?? []).filter((s) => fitsList(list.kind, s.category) && !have.has(sameNameKey(s.name))).slice(0, SUGGEST_SHOWN);
  if (shown.length === 0) return null;
  const add = (s: ShoppingSuggestionDto) => {
    hapticTap();
    void engine.mutate({ type: "item.create", id: newId(), listId: list.id, name: s.name, qty: null, unit: null });
  };
  return (
    <section className="suggest" aria-label={t("suggestTitle")}>
      <div className="suggest-title">⏳ {t("suggestTitle")}</div>
      <div className="chips">
        {shown.map((s) => (
          <button key={s.productId} type="button" className="chip" onClick={() => add(s)}>
            ＋ {s.name}
          </button>
        ))}
      </div>
    </section>
  );
}

function AddItem({ listId }: { listId: string }) {
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = () => {
    const parsed = parseQuickItem(text);
    if (!parsed.name) return;
    hapticTap();
    void engine.mutate({ type: "item.create", id: newId(), listId, name: parsed.name, qty: parsed.qty, unit: parsed.unit });
    setText("");
    inputRef.current?.focus();
  };

  return (
    <form
      className="add-item"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <input
        ref={inputRef}
        className="input"
        placeholder={t("addItem")}
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={120}
        enterKeyHint="done"
      />
      <button className="btn btn-primary add-btn" type="submit" disabled={!text.trim()} aria-label="add">
        +
      </button>
    </form>
  );
}

function ItemRow(props: {
  item: ItemDto;
  token: string;
  onToggle: (item: ItemDto) => void;
  onEdit: (item: ItemDto) => void;
  checkedBy?: string | null;
}) {
  const { item } = props;
  const qty = item.qty != null ? `${item.qty}${item.unit ? ` ${unitLabel(item.unit)}` : ""}` : "";
  return (
    <div className={item.isChecked ? "item checked" : "item"}>
      <button
        className="checkbox"
        role="checkbox"
        aria-checked={item.isChecked}
        aria-label={item.name}
        onClick={() => props.onToggle(item)}
      >
        {item.isChecked ? "✓" : ""}
      </button>
      <div className="item-body" onClick={() => props.onEdit(item)}>
        <div className="item-name">
          {item.name}
          {qty && <span className="item-qty">{qty}</span>}
        </div>
        {(item.comment || props.checkedBy) && (
          <div className="item-meta">
            {item.comment}
            {item.comment && props.checkedBy ? " · " : ""}
            {props.checkedBy ? t("checkedBy", { name: props.checkedBy }) : ""}
          </div>
        )}
      </div>
      {item.photoFileId && <img className="item-thumb" src={fileUrl(item.photoFileId, props.token)} alt="" loading="lazy" />}
    </div>
  );
}

function RenameSheet({ list, onClose }: { list: ListDto; onClose: () => void }) {
  const [name, setName] = useState(list.name);
  return (
    <Sheet title={t("rename")} onClose={onClose}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() && name.trim() !== list.name) void engine.mutate({ type: "list.update", id: list.id, name: name.trim() });
          onClose();
        }}
      >
        <input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        <button className="btn btn-primary" type="submit">
          {t("save")}
        </button>
      </form>
    </Sheet>
  );
}
